import ApplicationController from './application_controller';
import Logger from '../lib/logger';
// Warns as a bid's price is typed when the supplier price index finds it no market price, or the work of a
// named typo — a decimal slip, dollars entered as reais, an annual price entered as monthly, Mbps and Gbps
// confused — and asks once more before the form saves it (SPI-23). The server decides (BidPriceCheck, through
// /bid_price_check), so the rules live in one place; a dear or cheap price no typo explains passes, since it
// may be real.
//
// Declared on one bid's own fields (shared/_supplier_quote_fields), so a quote with several bids checks each.
// It needs the quote saved, since the price depends on its site. A check that fails never blocks a save; a
// save made before the check has answered waits for it; a bid removed from the form is not in the way.
class default_1 extends ApplicationController {
    constructor() {
        super(...arguments);
        this.warning = null;
        this.confirmed = false;
        this.form = null;
        this.scheduled = false;
        this.debouncedCheck = null;
        this.request = null;
        this.inFlight = false;
        this.sequence = 0;
    }
    connect() {
        super.connect();
        this.debouncedCheck = this.debounce(() => {
            this.scheduled = false;
            void this.runCheck();
        }, this.delayValue);
        this.form = this.element.closest('form');
        // Capture, so a confirmation is asked before Turbo takes the submission; removed again by cleanup().
        if (this.form)
            this.addEventListener(this.form, 'submit', this.handleSubmit, true);
    }
    disconnect() {
        this.debouncedCheck?.cancel();
        this.request?.abort();
        super.disconnect();
    }
    // Any field the price depends on changed: a confirmation given for the old figures no longer stands, and an
    // answer still on its way for them must not land.
    check() {
        this.confirmed = false;
        this.request?.abort();
        this.scheduled = true;
        this.debouncedCheck?.();
    }
    async runCheck() {
        const sequence = ++this.sequence;
        if (!this.hasMrcTarget || !this.urlValue)
            return;
        const mrc = this.mrcTarget.value.trim();
        if (mrc === '' || !(Number(mrc) > 0)) {
            this.show(null);
            return;
        }
        this.request?.abort();
        this.request = new AbortController();
        this.inFlight = true;
        try {
            const response = await fetch(`${this.urlValue}?${this.params(mrc).toString()}`, {
                headers: { Accept: 'application/json' },
                credentials: 'same-origin',
                signal: this.request.signal
            });
            if (!response.ok)
                throw new Error(`HTTP ${response.status}`);
            const data = (await response.json());
            if (sequence === this.sequence)
                this.show(data.warning ?? null);
        }
        catch (error) {
            if (error instanceof DOMException && error.name === 'AbortError')
                return;
            Logger.warn('Bid price check failed', { controller: this.constructor.name, error: String(error) });
            if (sequence === this.sequence)
                this.show(null);
        }
        finally {
            if (sequence === this.sequence)
                this.inFlight = false;
        }
    }
    params(mrc) {
        const params = new URLSearchParams({ quote_id: String(this.quoteIdValue), mrc });
        const fields = [
            ['service_type_id', this.hasServiceTarget, () => this.serviceTarget.value],
            ['bandwidth_id', this.hasBandwidthTarget, () => this.bandwidthTarget.value],
            ['access_type_id', this.hasAccessTarget, () => this.accessTarget.value],
            ['term', this.hasTermTarget, () => this.termTarget.value],
            ['currency', this.hasCurrencyTarget, () => this.currencyTarget.value]
        ];
        fields.forEach(([name, present, value]) => {
            if (present && value() !== '')
                params.set(name, value());
        });
        return params;
    }
    // Asks once: the warning says what looks wrong, and saving again keeps the price as typed. A save made while
    // the check is still waiting or on its way is held, checked at once, then let through or asked about.
    handleSubmit(event) {
        if (this.removed())
            return;
        if (this.scheduled || this.inFlight) {
            event.preventDefault();
            event.stopImmediatePropagation();
            this.debouncedCheck?.cancel();
            this.scheduled = false;
            const submitter = event.submitter ?? undefined;
            void this.runCheck().then(() => (this.warning ? this.ask() : this.form?.requestSubmit(submitter)));
            return;
        }
        if (!this.warning || this.confirmed)
            return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.ask();
    }
    ask() {
        this.confirmed = true;
        if (this.hasWarningTarget)
            this.warningTarget.textContent = `${this.warning} Save again to keep it as typed.`;
        if (this.hasMrcTarget)
            this.mrcTarget.focus();
    }
    // A saved bid removed from the form is only hidden and marked for destruction (nested-form#remove), so its
    // warning must not hold the save.
    removed() {
        const destroy = this.element.querySelector('input[name*="_destroy"]');
        return this.element.style.display === 'none' || destroy?.value === '1';
    }
    show(warning) {
        this.warning = warning;
        if (!this.hasWarningTarget)
            return;
        this.warningTarget.hidden = !warning;
        this.warningTarget.textContent = warning ?? '';
    }
}
default_1.targets = ['mrc', 'service', 'bandwidth', 'access', 'term', 'currency', 'warning'];
default_1.values = { url: String, quoteId: Number, delay: { type: Number, default: 400 } };
export default default_1;
